import React, {useState} from 'react';
import classNames from 'classnames';

import {Text, Button, Icon, Anchor} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {history} from 'client/history';

import TermsFooter from './TermsFooter';

type AccountType = 'educator' | 'student';

interface AccountTypeCardProps {
  title: string;
  subtitle?: string;
  icon: IconProp;
  selected: boolean;
  onClick: () => void;
}

function AccountTypeCard({title, subtitle, icon, selected, onClick}: AccountTypeCardProps) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      className={classNames(
        'flex w-full cursor-pointer items-center gap-x2 rounded-full py-x1-half pr-x3 pl-x2 transition-colors m:max-w-[432px] m:py-x2',
        {
          'border-2 border-selected background-brand-subtle-hover': selected,
          'border border-primary background-primary hover:background-subtle': !selected
        }
      )}
      onClick={onClick}
    >
      <div className='flex h-x6-quarter w-x6-quarter shrink-0 items-center justify-center rounded-full background-brand'>
        <Icon icon={icon} className='text-xl content-primary-inverse' />
      </div>
      <div className='flex flex-1 flex-col items-start justify-center gap-x0-half text-left'>
        <Text bold size='xl'>
          {title}
        </Text>
        {subtitle && (
          <Text size='s' color='secondary'>
            {subtitle}
          </Text>
        )}
      </div>
      <Icon icon='chevron-right' />
    </button>
  );
}

interface AccountTypeStepProps {
  onTypeChange?: (type: AccountType | null) => void;
}

export default function AccountTypeStep({onTypeChange}: AccountTypeStepProps) {
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);

  const handleSelect = (type: AccountType) => {
    setSelectedType(type);
    onTypeChange?.(type);
  };

  const handleContinue = () => {
    if (selectedType) {
      history.pushState(null, `/signup?accountType=${selectedType}`);
    }
  };

  return (
    <div className='flex w-full flex-col items-center justify-center gap-x5'>
      <Text className='text-xxl font-black'>Get started as a(n)...</Text>

      <div className='flex w-full flex-col items-center gap-x1-half'>
        <AccountTypeCard
          title='Educator'
          subtitle='K-12, Homeschool Educators, and Tutors'
          icon={['far', 'chalkboard-user']}
          selected={selectedType === 'educator'}
          onClick={() => handleSelect('educator')}
        />
        <AccountTypeCard
          title='Student'
          icon={['far', 'backpack']}
          selected={selectedType === 'student'}
          onClick={() => handleSelect('student')}
        />
      </div>
      <div className='flex w-full flex-col items-center gap-x1-half'>
        <Button
          className='w-full max-w-[432px]'
          disabled={!selectedType}
          onClick={handleContinue}
          roundness='pill'
          size='l'
        >
          Continue
        </Button>
        <Text className='text-center'>
          Already have an account?{' '}
          <Anchor to='/log-in' className='content-brand'>
            Log in
          </Anchor>
        </Text>
      </div>

      <TermsFooter />
    </div>
  );
}
